// Shared helpers for generating project summaries (plain text + HTML)
export const defaultNormalizeDateValue = (value) => {
  if (!value) return null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value;
  }
  if (typeof value?.toDate === "function") {
    return value.toDate();
  }
  if (value?.seconds) {
    return new Date(value.seconds * 1000);
  }
  const asDate = new Date(value);
  return Number.isNaN(asDate.getTime()) ? null : asDate;
};

export const formatSummaryCurrency = (value, currency = "GBP") => {
  let parsedValue = value;
  if (typeof value === "string") {
    parsedValue = parseFloat(value.replace(/,/g, ""));
  }
  const num =
    typeof parsedValue === "number" ? parsedValue : parseFloat(parsedValue);
  if (!Number.isFinite(num)) {
    return currency === "GBP" ? "£0.00" : `${currency} 0.00`;
  }
  const hasFraction = Math.abs(num % 1) > 1e-9;
  const formatted = new Intl.NumberFormat("en-GB", {
    minimumFractionDigits: hasFraction ? 2 : 0,
    maximumFractionDigits: hasFraction ? 2 : 0,
  }).format(num);

  if (currency === "GBP") {
    return `£${formatted}`;
  }
  return `${currency} ${formatted}`;
};

const taskDescriptionMap = {
  DSS: "Director-Shareholder Search",
  SR: "Screening Report",
  BC: "Background Checks",
  PS: "Property Ownership Enquiries",
  VS: "Vehicle Ownership Enquiries",
  IMEQ: "Immigration Enquiries",
  BEQ: "Banking Relationship Enquiries",
  FL: "Corporate Records Report",
  SS: "Subsidiary Search",
  EQ: "Human Source Enquiries",
  LC: "Litigation Enquiries",
  MA: "Media Archive Search",
  RPS: "Reverse Property Ownership Enquiries",
  SV: "Site Visit",
  VES: "Vessle Ownership Enquiries",
};

export const buildProjectSummary = (
  project,
  { normalizeDateValue = defaultNormalizeDateValue, currency = "GBP" } = {}
) => {
  if (!project) return { plainText: "", htmlText: "" };

  const currencyLabel = (currency || "GBP").toUpperCase();
  const isAED = currencyLabel === "AED";
  const isBoth = currencyLabel === "GBP_AED";

  const toNumber = (val) => {
    if (typeof val === "number") return Number.isFinite(val) ? val : null;
    if (typeof val === "string") {
      const parsed = parseFloat(val.replace(/,/g, ""));
      return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
  };

  const tasks = Array.isArray(project.tasks)
    ? project.tasks.filter(Boolean)
    : [];
  const yourProjectRef = (() => {
    const val = project.paymentNotes;
    if (val === null || val === undefined) return "N/A";
    const asString = String(val).trim();
    return asString.length ? asString : "N/A";
  })();
  const firstName = (() => {
    const val = project.poc;
    if (!val) return "there";
    const parts = String(val).trim().split(/\s+/);
    return parts[0] || "there";
  })();

  const formatDate = (value) => {
    const date = normalizeDateValue(value);
    if (!date) return "-";
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const taskRows = tasks.map((task, index) => {
    const incomeGbp = toNumber(task?.income) ?? 0;
    const incomeAed = toNumber(task?.incomeAED) ?? 0;
    const income = isAED ? incomeAed : incomeGbp;

    const requirementRaw = task?.task ? task.task.toString().trim() : "";
    const requirement =
      taskDescriptionMap[requirementRaw] || requirementRaw || "-";
    const deliveryTimeline = formatDate(task?.dueDate);

    return {
      taskNo: index + 1,
      subject: task?.subject ? task.subject.toString() : "-",
      country: task?.country ? task.country.toString() : "-",
      taskType: requirement,
      deliveryTimeline,
      fee: income,
      feeGbp: incomeGbp,
      feeAed: incomeAed,
    };
  });

  const sumTaskFees = (extractor) =>
    taskRows.reduce(
      (sum, row) =>
        sum + (Number.isFinite(extractor(row)) ? extractor(row) : 0),
      0
    );

  const getTotalForCurrency = (code) => {
    const codeUpper = (code || "").toUpperCase();
    if (codeUpper === "AED") {
      const tasksTotal = sumTaskFees((row) => row.feeAed);
      if (tasksTotal > 0) return tasksTotal;
      const incomeVal = toNumber(project.incomeAED);
      return Number.isFinite(incomeVal) && incomeVal > 0 ? incomeVal : 0;
    }
    // default GBP
    const tasksTotal = sumTaskFees((row) => row.feeGbp);
    if (tasksTotal > 0) return tasksTotal;
    const incomeVal = toNumber(project.income);
    return Number.isFinite(incomeVal) && incomeVal > 0 ? incomeVal : 0;
  };

  const totalFeeGbp = getTotalForCurrency("GBP");
  const totalFeeAed = getTotalForCurrency("AED");
  const singleTotal = isAED ? totalFeeAed : totalFeeGbp;

  const headers = isBoth
    ? [
        "#",
        "Jurisdiction",
        "Requirement",
        "Subject",
        "TAT",
        "Fee (GBP)",
        "Fee (AED)",
      ]
    : [
        "#",
        "Jurisdiction",
        "Requirement",
        "Subject",
        "TAT",
        `Fee (${currencyLabel})`,
      ];

  const rowStrings = taskRows.map((row) => {
    const base = [
      String(row.taskNo ?? "-"),
      row.country || "-",
      row.taskType || "-",
      row.subject || "-",
      row.deliveryTimeline || "-",
    ];
    if (isBoth) {
      return [
        ...base,
        formatSummaryCurrency(row.feeGbp, "GBP"),
        formatSummaryCurrency(row.feeAed, "AED"),
      ];
    }
    return [...base, formatSummaryCurrency(row.fee, currencyLabel)];
  });

  const columnWidths = headers.map((header, index) => {
    const maxRowWidth = rowStrings.reduce((max, row) => {
      const cell = row[index] || "";
      return Math.max(max, cell.length);
    }, 0);
    return Math.max(header.length, maxRowWidth);
  });

  const padCell = (text, width) => String(text || "-").padEnd(width + 2, " ");

  const headerRow = headers
    .map((header, idx) => padCell(header, columnWidths[idx]))
    .join("| ")
    .trimEnd();

  const bodyRows =
    rowStrings.length > 0
      ? rowStrings.map((row) =>
          row
            .map((cell, idx) => padCell(cell, columnWidths[idx]))
            .join("| ")
            .trimEnd()
        )
      : ["(no tasks available)"];

  const plainLines = [
    `Hi ${firstName},`,
    "",
    "Please see below order confirmation.",
    "",
    `Our Project Reference: ${project.projectRef || "-"}`,
    `Your Project Reference: ${yourProjectRef}`,
    "",
    "Scope of Work:",
    headerRow,
    ...bodyRows,
    "",
    ...(isBoth
      ? [
          `Total Fee (GBP): ${formatSummaryCurrency(totalFeeGbp, "GBP")}`,
          `Total Fee (AED): ${formatSummaryCurrency(totalFeeAed, "AED")}`,
        ]
      : [`Total Fee: ${formatSummaryCurrency(singleTotal, currencyLabel)}`]),
  ];

  const escapeHtml = (value) => {
    if (value === null || value === undefined) return "";
    return String(value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  };

  const htmlRows =
    taskRows.length > 0
      ? taskRows
          .map((row) => {
            const tatCellStyle =
              "border:1px solid #d1d5db;padding:8px 10px;min-width:110px;text-align:center;vertical-align:middle;";
            const feeCellStyle =
              "border:1px solid #d1d5db;padding:8px 10px;min-width:110px;text-align:center;vertical-align:middle;";
            const feeCells = isBoth
              ? `<td style="${feeCellStyle}">${escapeHtml(
                  formatSummaryCurrency(row.feeGbp, "GBP")
                )}</td>
            <td style="${feeCellStyle}">${escapeHtml(
                  formatSummaryCurrency(row.feeAed, "AED")
                )}</td>`
              : `<td style="${feeCellStyle}">${escapeHtml(
                  formatSummaryCurrency(row.fee, currencyLabel)
                )}</td>`;

            return `<tr>
              <td style="border:1px solid #d1d5db;padding:8px 10px;text-align:center;vertical-align:middle;">${escapeHtml(
                row.taskNo || "-"
              )}</td>
              <td style="border:1px solid #d1d5db;padding:8px 10px;text-align:center;vertical-align:middle;">${escapeHtml(
                row.country || "-"
              )}</td>
              <td style="border:1px solid #d1d5db;padding:8px 10px;text-align:center;vertical-align:middle;">${escapeHtml(
                row.taskType || "-"
              )}</td>
              <td style="border:1px solid #d1d5db;padding:8px 10px;text-align:center;vertical-align:middle;">${escapeHtml(
                row.subject || "-"
              )}</td>
              <td style="${tatCellStyle}">${escapeHtml(
              row.deliveryTimeline || "-"
            )}</td>
              ${feeCells}
            </tr>`;
          })
          .join("")
      : `<tr>
            <td colspan="${
              isBoth ? 7 : 6
            }" style="border:1px solid #d1d5db;padding:10px;text-align:center;color:#6b7280;">No tasks available</td>
          </tr>`;

  const feeHeaderCells = isBoth
    ? `<th style="border:1px solid #d1d5db; padding:9px 10px; text-align:center; vertical-align:middle; min-width:100px;">Fee (GBP)</th>
        <th style="border:1px solid #d1d5db; padding:9px 10px; text-align:center; vertical-align:middle; min-width:100px;">Fee (AED)</th>`
    : `<th style="border:1px solid #d1d5db; padding:9px 10px; text-align:center; vertical-align:middle; min-width:100px;">Fee (${currencyLabel})</th>`;

  const totalsHtml = isBoth
    ? `<strong>Total Fee (GBP):</strong> ${escapeHtml(
        formatSummaryCurrency(totalFeeGbp, "GBP")
      )}<br/>
    <strong>Total Fee (AED):</strong> ${escapeHtml(
      formatSummaryCurrency(totalFeeAed, "AED")
    )}`
    : `<strong>Total Fee:</strong> ${escapeHtml(
        formatSummaryCurrency(singleTotal, currencyLabel)
      )}`;

  const html = `
<div style="font-family: Arial, sans-serif; color: #0f172a; font-size: 14px; line-height: 1.6;">
  <p style="margin:0 0 12px 0;">Hi ${escapeHtml(firstName)},</p>
  <p style="margin:0 0 12px 0;">Please see below order confirmation:</p>
  <p style="margin:0 0 12px 0;">
    <strong>Our Project Reference:</strong> ${escapeHtml(
      project.projectRef || "-"
    )}<br/>
    <strong>Your Project Reference:</strong> ${escapeHtml(yourProjectRef)}
  </p>
  <p><strong>Scope of Work:</strong></p>
  <table style="border-collapse: collapse; width: 100%; max-width: 720px; border:1px solid #d1d5db;">
    <thead>
      <tr style="background-color:#0f766e; color:#f8fafc;">
        <th style="border:1px solid #d1d5db; padding:9px 10px; text-align:center; vertical-align:middle;">#</th>
        <th style="border:1px solid #d1d5db; padding:9px 10px; text-align:center; vertical-align:middle;">Jurisdiction</th>
        <th style="border:1px solid #d1d5db; padding:9px 10px; text-align:center; vertical-align:middle;">Requirement</th>
        <th style="border:1px solid #d1d5db; padding:9px 10px; text-align:center; vertical-align:middle;">Subject</th>
        <th style="border:1px solid #d1d5db; padding:9px 10px; text-align:center; vertical-align:middle; min-width:110px;">TAT</th>
        ${feeHeaderCells}
      </tr>
    </thead>
    <tbody>
      ${htmlRows}
    </tbody>
  </table>
  <p style="margin-top:16px;">
    ${totalsHtml}
  </p>
</div>
      `.trim();

  return {
    plainText: plainLines.join("\n"),
    htmlText: html,
  };
};
